import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import OnecContractorPicker from '@/components/OnecContractorPicker';
import SkudPersonPicker from '@/components/SkudPersonPicker';
import { ApiError, api } from '@/lib/api';
import { useWsEvent } from '@/auth/WsProvider';
import { useCan } from '@/auth/AuthContext';

const EMPTY = {
  familyName: '',
  firstName: '',
  midName: '',
  sex: '',
  birthdate: '',
  status: 'active',
  archiveDestination: '',
  phone: '',
  email: '',
  onecContractor: null,
  skudPerson: null,
};

/**
 * Универсальная модалка редактирования и создания ученика.
 *
 *   mode='create' studentId=null    → пустая форма, POST /api/students
 *   mode='edit'   studentId=NN      → загружаем GET /api/students/NN, PUT обратно
 *
 * onSaved(student) — фронт может обновить список без ре-фетча
 */
export default function StudentEditModal({ mode, studentId, onClose, onSaved, onDeleted }) {
  const can = useCan();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateConfirm, setDuplicateConfirm] = useState(null); // { duplicates: [...] } или null

  // Автокомплит «куда переводится»: ранее использованные варианты, чтобы
  // одна и та же школа не появлялась в пяти написаниях. Грузим лениво,
  // когда выбран статус «Архив».
  const [destOptions, setDestOptions] = useState(null);
  useEffect(() => {
    if (data.status !== 'archive' || destOptions !== null) return;
    api.get('/api/students/archive-destinations')
      .then((res) => setDestOptions((res.rows || []).map((r) => r.value)))
      .catch(() => setDestOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.status]);

  useEffect(() => {
    if (mode !== 'edit' || !studentId) return;
    let aborted = false;
    setLoading(true);
    setError(null);
    api.get(`/api/students/${studentId}`)
      .then((res) => {
        if (aborted) return;
        const s = res.student;
        setData({
          familyName: s.familyName || '',
          firstName:  s.firstName  || '',
          midName:    s.midName    || '',
          sex:        s.sex        || '',
          birthdate:  s.birthdate  || '',
          status:     s.status     || 'active',
          archiveDestination: s.archiveDestination || '',
          phone:      s.phone      || '',
          email:      s.email      || '',
          onecContractor: s.onecContractor || null,
          skudPerson:     s.skudPerson     || null,
        });
      })
      .catch((e) => { if (!aborted) setError(e instanceof ApiError ? e.message : 'Ошибка загрузки'); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [mode, studentId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Когда node закончил push нашего контрагента — обновляем строку в форме
  useWsEvent('onec.contractor.updated', (msg) => {
    setData((d) => {
      if (!msg?.contractor || !d.onecContractor) return d;
      if (d.onecContractor.id !== msg.contractor.id) return d;
      return { ...d, onecContractor: msg.contractor };
    });
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const buildPayload = () => ({
    familyName: data.familyName.trim(),
    firstName:  data.firstName.trim(),
    midName:    data.midName.trim(),
    sex:        data.sex || null,
    birthdate:  data.birthdate || null,
    status:     data.status,
    archiveDestination: data.status === 'archive' ? (data.archiveDestination.trim() || null) : null,
    phone:      data.phone.trim() || null,
    email:      data.email.trim() || null,
    onecContractorId: data.onecContractor?.id || null,
    skudPersonId:     data.skudPerson?.skudId || null,
  });

  const doSave = async (payload) => {
    const res = mode === 'create'
      ? await api.post('/api/students', payload)
      : await api.put(`/api/students/${studentId}`, payload);
    onSaved?.(res.student);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await doSave(buildPayload());
    } catch (err) {
      // 409 possible_duplicate — показываем подтверждение и даём шанс пересохранить
      if (err instanceof ApiError && err.status === 409 && err.code === 'possible_duplicate') {
        const dups = err.payload?.error?.duplicates || [];
        setDuplicateConfirm({ duplicates: dups });
      } else {
        setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmCreateAnyway = async () => {
    setSaving(true); setError(null);
    try {
      await doSave({ ...buildPayload(), confirmDuplicate: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
      setDuplicateConfirm(null);
    }
  };

  // Имя ученика — для подсказки при создании контрагента в 1С
  const fullName = `${data.familyName} ${data.firstName}${data.midName ? ' ' + data.midName : ''}`.trim();

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{mode === 'create' ? 'Новый ученик' : 'Редактирование ученика'}</h2>
          <button className="sd-close" onClick={onClose} aria-label="Закрыть"><Icon name="x" size={14} /></button>
        </div>

        {loading ? (
          <div style={{ padding: 32, color: 'var(--fg-muted)' }}>Загрузка…</div>
        ) : (
          <form onSubmit={submit} className="sd-edit__form" noValidate>
            <div className="sd-edit__body">
              <div className="sd-edit__field">
                <label className="sd-edit__label">Фамилия *</label>
                <input
                  className="sd-edit__input" required autoFocus
                  value={data.familyName}
                  onChange={(e) => set('familyName', e.target.value)}
                />
              </div>
              <div className="sd-edit__field">
                <label className="sd-edit__label">Имя *</label>
                <input
                  className="sd-edit__input" required
                  value={data.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                />
              </div>

              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Отчество</label>
                <input
                  className="sd-edit__input"
                  value={data.midName}
                  onChange={(e) => set('midName', e.target.value)}
                />
              </div>

              <div className="sd-edit__field">
                <label className="sd-edit__label">Пол</label>
                <select className="sd-edit__select" value={data.sex || ''} onChange={(e) => set('sex', e.target.value)}>
                  <option value="">— не выбрано —</option>
                  <option value="male">мужской</option>
                  <option value="female">женский</option>
                </select>
              </div>
              <div className="sd-edit__field">
                <label className="sd-edit__label">Дата рождения</label>
                <input
                  className="sd-edit__input"
                  type="date"
                  value={data.birthdate || ''}
                  onChange={(e) => set('birthdate', e.target.value)}
                />
              </div>

              <div className="sd-edit__field">
                <label className="sd-edit__label">Телефон</label>
                <input
                  className="sd-edit__input"
                  type="tel"
                  value={data.phone}
                  onChange={(e) => set('phone', e.target.value)}
                />
              </div>
              <div className="sd-edit__field">
                <label className="sd-edit__label">Email</label>
                <input
                  className="sd-edit__input"
                  type="email"
                  value={data.email}
                  onChange={(e) => set('email', e.target.value)}
                />
              </div>

              <div className="sd-edit__field">
                <label className="sd-edit__label">Статус</label>
                <select className="sd-edit__select" value={data.status} onChange={(e) => set('status', e.target.value)}>
                  <option value="active">Активен</option>
                  <option value="archive">Архив</option>
                </select>
              </div>

              {data.status === 'archive' && (
                <div className="sd-edit__field sd-edit__field--full">
                  <label className="sd-edit__label">Куда переводится</label>
                  <input
                    className="sd-edit__input"
                    list="archive-destinations"
                    value={data.archiveDestination}
                    onChange={(e) => set('archiveDestination', e.target.value)}
                    placeholder="например: Школа № 57, переезд в другой город"
                  />
                  <datalist id="archive-destinations">
                    {(destOptions || []).map((v) => <option key={v} value={v} />)}
                  </datalist>
                  <div className="sd-edit__hint">
                    Подсказки — из ранее введённых вариантов: выбирайте существующий,
                    если школа уже встречалась, чтобы не плодить разные написания.
                  </div>
                </div>
              )}

              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Контрагент в 1С</label>
                <OnecContractorPicker
                  value={data.onecContractor}
                  onChange={(v) => set('onecContractor', v)}
                  defaultName={fullName}
                />
              </div>

              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Человек в СКУД</label>
                <SkudPersonPicker
                  value={data.skudPerson}
                  role="student"
                  onChange={(p) => set('skudPerson', p)}
                />
                <div className="sd-edit__hint">
                  Список фильтруется по подразделению «Ученики» из настроек СКУД.
                  При переводе ученика в архив все его пропуска автоматически
                  заблокируются.
                </div>
              </div>
            </div>

            <div className="sd-edit__foot">
              {/* Удаление — только для пустых карточек (ошибочно созданных):
                  без договоров, платежей, начислений и зачислений. Бэкенд
                  проверит и откажет с перечислением, что мешает. */}
              {mode === 'edit' && can('students', 'delete') && (
                <button
                  type="button"
                  className="btn btn--sm"
                  style={{ color: 'var(--red-fg, #dc2626)' }}
                  disabled={saving}
                  onClick={async () => {
                    const fio = [data.familyName, data.firstName, data.midName].filter(Boolean).join(' ');
                    if (!window.confirm(
                      `Удалить карточку ученика «${fio}»?\n\n`
                      + 'Доступно только для пустых карточек — без договоров, платежей, '
                      + 'начислений и зачислений. Для учеников с историей используйте архив.'
                    )) return;
                    setSaving(true); setError(null);
                    try {
                      await api.delete(`/api/students/${studentId}`);
                      onDeleted?.();
                      onClose();
                    } catch (err) {
                      setError(err instanceof ApiError ? err.message : 'Не удалось удалить');
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Удалить карточку
                </button>
              )}
              {error && <div className="sd-edit__error">{error}</div>}
              <div className="sd-edit__foot-spacer" />
              <button type="button" className="btn btn--sm" onClick={onClose}>Отмена</button>
              <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
                {saving ? 'Сохраняем…' : (mode === 'create' ? 'Создать' : 'Сохранить')}
              </button>
            </div>
          </form>
        )}

        {duplicateConfirm && (
          <DuplicateConfirmDialog
            duplicates={duplicateConfirm.duplicates}
            saving={saving}
            onCancel={() => setDuplicateConfirm(null)}
            onConfirm={confirmCreateAnyway}
          />
        )}
      </div>
    </div>
  );
}

function DuplicateConfirmDialog({ duplicates, saving, onCancel, onConfirm }) {
  const fmtDate = (iso) => iso ? iso.split('-').reverse().join('.') : '';
  return (
    <div
      className="sd-overlay"
      style={{ zIndex: 100 }}
      onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onCancel(); }}
    >
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true" style={{ maxWidth: 480 }}>
        <div className="sd-edit__head">
          <h2>Возможный дубль</h2>
          <button className="sd-close" onClick={onCancel} aria-label="Закрыть"><Icon name="x" size={14} /></button>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--fg)' }}>
            В системе уже есть {duplicates.length === 1 ? 'ученик' : 'ученики'} с таким же ФИО и датой рождения:
          </p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13, color: 'var(--fg)' }}>
            {duplicates.map((d) => (
              <li key={d.id} style={{ marginBottom: 4 }}>
                <b>{d.fullName}</b>
                {d.birthdate && <span style={{ color: 'var(--fg-muted)' }}> · {fmtDate(d.birthdate)}</span>}
                <span style={{ color: 'var(--fg-faint)' }}> · #{d.id}</span>
              </li>
            ))}
          </ul>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--fg-muted)' }}>
            Всё равно создать нового?
          </p>
        </div>
        <div className="sd-edit__foot">
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onCancel} disabled={saving}>Отмена</button>
          <button type="button" className="btn btn--primary btn--sm" onClick={onConfirm} disabled={saving}>
            {saving ? 'Создаём…' : 'Да, всё равно создать'}
          </button>
        </div>
      </div>
    </div>
  );
}
